import { __assign } from "tslib";
import React, { useRef, createContext, useContext, useCallback } from 'react';
// For react 17 we need to use shim
//@ts-ignore
import { useSyncExternalStore } from 'use-sync-external-store/shim';
/**
 * Function to create a fast context hooks, with a provider and hooks to get and set the store.
 * @param initialState Initial state of the store.
 * @returns Provider and hooks to get and set the store.
 */
export function createFastContext(initialState) {
    var initState = initialState;
    function useStoreData() {
        var store = useRef(initState);
        var get = useCallback(function () { return store.current; }, []);
        var subscribers = useRef(new Set());
        var set = useCallback(function (value) {
            store.current = __assign(__assign({}, store.current), value);
            subscribers.current.forEach(function (callback) { return callback(); });
        }, []);
        var subscribe = useCallback(function (callback) {
            subscribers.current.add(callback);
            return function () { return subscribers.current.delete(callback); };
        }, []);
        return {
            get: get,
            set: set,
            subscribe: subscribe,
        };
    }
    var StoreContext = createContext(null);
    /**
     * Context provider to provide the store to all the children.
     */
    function Provider(_a) {
        var children = _a.children, defaultState = _a.defaultState;
        if (defaultState) {
            initState = __assign(__assign({}, initialState), defaultState);
        }
        return React.createElement(StoreContext.Provider, { value: useStoreData() }, children);
    }
    /**
     * Utility function to check if the context is available.
     */
    function useContextAvailable() {
        var context = useContext(StoreContext);
        return !!context;
    }
    /**
     * Function to get the value and and setter function from store.
     * Note: For performance reasons, it is recommended to return only the required value from the selector function.
     * @param selector Function to select the value from store.
     * @returns state and setter function.
     * @example const [items, setStore] = useStore(s => s.items)
     * setStore({ items: [foo: "bar"] })
     */
    function useStore(selector) {
        var store = useContext(StoreContext);
        if (!store) {
            throw new Error('Store not found - please wrap your component with a Provider');
        }
        var state = useSyncExternalStore(store.subscribe, function () { return selector(store.get()); }, function () { return selector(initState); });
        return [state, store.set];
    }
    /**
     * Function to get only the value from store.
     * @param selector Function to select the value from store.
     * @returns Value from store.
     * @example const items = useStoreGetter(s => s.items)
     */
    function useStoreGetter(selector) {
        return useStore(selector)[0];
    }
    /**
     * Function to get only the setter function from store.
     * @returns Setter function from store.
     * @example const updateStore = useStoreSetter();
     * updateStore({ items: [foo: "bar"] })
     */
    var useStoreSetter = function () { return useStore(function () { return null; })[1]; };
    return {
        Provider: Provider,
        useStore: useStore,
        useStoreGetter: useStoreGetter,
        useStoreSetter: useStoreSetter,
        useContextAvailable: useContextAvailable,
    };
}
export default createFastContext;
//# sourceMappingURL=createFastContext.js.map